import {Injectable} from "@angular/core";
import {FormGeneratorService} from "../../components/common/fields/form-generator.service";
import {FieldDefinition} from "../../model/field-definition";
import {Observable, of, zip} from "rxjs";
import {ProfileService} from "../../services/profile.service";
import {Profile} from "../../model/profile";
import {map} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class UserTicketFormGeneratorService implements FormGeneratorService {

  profile: Profile;

  constructor(private profileService: ProfileService) {
  }

  patchValuesFromInstance(currentValues: any, versions: any): void {
    if(currentValues.installId) {
      if(currentValues.installId === 'none') {
        currentValues.hosting = '';
        currentValues.install = 'pas de fiche d\'install';
      } else {
        // From instance, we can auto-select queue, product and version
        const instance = this.profile.installs.find(i => i.crmId === currentValues.installId);

        if(currentValues.product !== instance.product.tag) {
          currentValues.product = instance.product.tag;
        }

        const versionsOpt = this.getVersionsOpt(versions, currentValues);

        // @ts-ignore
        const newVersion = versionsOpt.find(opt => {
          return instance.version.match(/[\d.]+/)[0] === opt.value.toLowerCase().match(/[\d.]+/)[0];
        }).key;
        if(currentValues.version !== newVersion) {
          currentValues.version = newVersion;
        }

        if(currentValues.hosting !== instance.hostingType) {
          currentValues.hosting = instance.hostingType;
        }
        if(currentValues.install !== instance.url) {
          currentValues.install = instance.url;
        }
        if(currentValues.exploit !== instance.exploitType) {
          currentValues.exploit = instance.exploitType;
        }
      }

    }
  }

  getQuestions(opt: {
    allowedProducts: string[],
    queues: Map<string, string>,
    versions: Map<string, string>,
    services: Map<string, string>,
    products: Map<string, string>,
    sla: Map<string, string>,
    cdtMode: boolean,
    values: any,
  }): Observable<FieldDefinition<any>[]> {
    return zip(
      this.profileService.getProfile()
    ).pipe(
      map(pairs => {
        this.profile = pairs[0];

        const productsOpt = this.getProductsOpt(opt.products, opt.cdtMode, opt.allowedProducts);
        // Check if product currently selected is still available
        if(opt.values.product && productsOpt.findIndex((a) => a.key === opt.values.product) === -1) {
          // Check the key in productsOpt
          delete opt.values.product;
        }
        opt.values.product = (productsOpt.length === 1) ? productsOpt[0].key: opt.values?.product;

        let instancesOpt = this.getInstancesOpt(pairs[0], productsOpt.map(s => s.key.toLowerCase()));
        if(instancesOpt.length === 0 && opt.values.queue) {
          instancesOpt = [{key: 'none', value: 'Information manquante, merci de remplir les champs ci-dessous'}];
          opt.values.installId = 'none';
        } else if (instancesOpt.length === 1) {
          opt.values.installId = instancesOpt[0].key;
        } else if (instancesOpt.length > 1) {
          // Check if installId currently selected is still available
          if(opt.values.installId && instancesOpt.findIndex((a) => a.key === opt.values.installId) === -1) {
            // Check the key in instancesOpt
            delete opt.values.installId;
          }
        }

        const versionsOpt = this.getVersionsOpt(opt.versions, opt.values);
        // Check if product currently selected is still available
        if(opt.values.version && versionsOpt.findIndex((a) => a.key === opt.values.version) === -1) {
          // Check the key in versionsOpt
          delete opt.values.version;
        }
        opt.values.version = (versionsOpt.length === 1) ? versionsOpt[0].key: opt.values?.version;


        const servicesOpt = this.optionsFor(opt.services);
        // Check if product currently selected is still available
        if(opt.values.service && servicesOpt.findIndex((a) => a.key === opt.values.service) === -1) {
          // Check the key in servicesOpt
          delete opt.values.service;
        }
        opt.values.service = (servicesOpt.length === 1) ? servicesOpt[0].key: opt.values?.service;

        // Service "Demande de mise à jour de patch" : une même demande peut
        // concerner plusieurs instances, on invite le client à le préciser
        const patchService = !!opt.values.service && !!opt.services[opt.values.service]
          && opt.services[opt.values.service].toLowerCase().indexOf('patch') !== -1;


        const slaOpt = this.optionsFor(opt.sla);
        // Check if product currently selected is still available
        if(opt.values.sla && slaOpt.findIndex((a) => a.key === opt.values.sla) === -1) {
          // Check the key in slaOpt
          delete opt.values.sla;
        }
        opt.values.sla = (slaOpt.length === 1) ? slaOpt[0].key: opt.values?.sla;

        let questions = [
          new FieldDefinition<string>({
            key: "queue",
            label: 'Pour',
            type: 'select',
            options: this.optionsFor(opt.queues),
            value: opt.values?.queue,
            required: true,
            order: 1
          }),
          new FieldDefinition<string>({
            key: "service",
            label: 'Service',
            type: 'select',
            options: servicesOpt,
            value: opt.values?.service,
            required: true,
            disabled: !opt.values.queue,
            info: patchService ? 'Si votre demande de patch concerne plusieurs instances (par ex. prod et test), merci de l\'indiquer dans le ticket.' : '',
            order: 5
          }),
          new FieldDefinition<string>({
            key: "sla",
            label: 'Niveau de priorité',
            type: 'select',
            options: slaOpt,
            value: opt.values?.sla,
            required: true,
            order: 6
          }),
          new FieldDefinition<string>({
            key: "subject",
            label: 'Sujet',
            type: 'text',
            value: opt.values?.subject,
            required: true,
            order: 7
          }),
          new FieldDefinition<string>({
            key: "body",
            label: 'Texte',
            type: 'editor',
            value: opt.values?.body,
            required: true,
            order: 8,
            payload: {
              minHeight: '15rem'
            }
          }),
        ];

        if((opt.values.installId === 'none' && opt.values.queue) || productsOpt.length > 1) {
          let order = 2;
          if(productsOpt.length === 1) {
            questions.push(new FieldDefinition<string>({
              key: "installId",
              label: 'Plateforme',
              type: 'alert',
              options: instancesOpt,
              required: true,
              value: opt.values?.installId,
              order: order++
            }));
          }
          questions.push(new FieldDefinition<string>({
              key: "product",
              label: 'Produit',
              type: 'select',
              options: productsOpt,
              value: opt.values?.product,
              required: true,
              order: order++
            }),
            new FieldDefinition<string>({
              key: 'version',
              label: 'Version',
              type: 'select',
              options: versionsOpt,
              value: opt.values?.version,
              required: true,
              info: versionsOpt.length > 0 ? 'Le numéro de version est affiché en bas de page de l\'application' : '',
              order: order++
            }));
        } else {
          questions.push(new FieldDefinition<string>({
            key: "installId",
            label: 'Plateforme',
            type: 'select',
            options: instancesOpt,
            value: opt.values?.installId,
            required: true,
            info: 'Si ces informations sont erronées, merci de l\'indiquer dans le ticket',
            order: 2
          }));
        }

        return questions.sort((a, b) => a.order - b.order);
      })
    )
  }

  getInstancesOpt(profile: Profile, allowedProducts: string[]): { key: string, value: string }[] {
    if(allowedProducts.length === 1) {
      return profile.installs.filter(i => allowedProducts.indexOf(i.product.tag.toLowerCase()) !== -1).map(install => {
        return {key: install.crmId, value: `${install.url} - ${install.exploitType} - Version ${install.version.match(/[\d.]+/)[0]}`};
      })
    } else {
      return [];
    }
  }

  getProductsOpt(products: Map<string, string>, cdtMode: boolean, allowedProducts: string[]): { key: string, value: string }[] {
    let productsOpt = this.optionsFor(products);
    if (cdtMode) {
      productsOpt = productsOpt.filter(o => allowedProducts.indexOf(o.key.toLowerCase()) !== -1);
    }
    return productsOpt;
  }

  getVersionsOpt(versions: Map<string, string>, values: any): { key: string, value: string }[] {
    return this.optionsFor(versions)
      .filter(c => c.key.indexOf('*') === -1 && c.key.startsWith(values?.product))
      .map(c => {
        // @ts-ignore
        c.value = c.value.replace(values?.product, '').replace(/\d+/g, n => +n + 100000);
        return c;
      })
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      })
      .map(c => {
        // @ts-ignore
        c.value = c.value.replace(/\d+/g, n => +n - 100000);
        return c;
      })
      .reverse();
  }

  optionsFor(obj: Map<string, string>): { key: string, value: string }[] {
    const opt = [];
    for (const [key, value] of Object.entries(obj)) {
      opt.push({
        key,
        value
      });
    }
    return opt;
  }
}
